import './index.css'
interface PrivacyPolicyProps {
  onBack?: () => void
}

export default function PrivacyPolicy({ onBack }: PrivacyPolicyProps) {
  return (
    <div className="bc-legal-page">
      <div className="bc-legal-container">
        <button className="bc-legal-back" onClick={onBack ?? (() => window.history.back())}>
          ← Voltar
        </button>

        <h1 className="bc-legal-title">Política de Privacidade</h1>
        <p className="bc-legal-date">Vigência: 16 de maio de 2026</p>

        <section className="bc-legal-section">
          <h2>1. Quem somos (Controlador)</h2>
          <p>
            O BagCash é um aplicativo de gestão financeira pessoal desenvolvido e operado por
            <strong> Paulo Santos</strong> ("BagCash", "nós" ou "controlador").
            Contato: <a href="mailto:privacidade@bagcash.app">privacidade@bagcash.app</a>
          </p>
          <p>
            Esta Política descreve como coletamos, usamos, armazenamos e protegemos seus dados
            pessoais em conformidade com a Lei Geral de Proteção de Dados (Lei 13.709/2018 — LGPD).
          </p>
        </section>

        <section className="bc-legal-section">
          <h2>2. Dados coletados e finalidades</h2>
          <table className="bc-legal-table">
            <thead>
              <tr>
                <th>Dado</th>
                <th>Finalidade</th>
                <th>Base legal (LGPD, Art. 7)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>E-mail e senha</td>
                <td>Autenticação e identificação da conta</td>
                <td>Execução de contrato (inciso II)</td>
              </tr>
              <tr>
                <td>Transações financeiras (descrição, valor, data, categoria)</td>
                <td>Exibição do painel financeiro e relatórios</td>
                <td>Execução de contrato (inciso II)</td>
              </tr>
              <tr>
                <td>Cartões de crédito (nome, últimos 4 dígitos, limite)</td>
                <td>Controle de faturas e saldos</td>
                <td>Execução de contrato (inciso II)</td>
              </tr>
              <tr>
                <td>E-mail (enviado ao Mercado Pago)</td>
                <td>Processamento de assinaturas pagas</td>
                <td>Execução de contrato (inciso II)</td>
              </tr>
              <tr>
                <td>Histórico de pagamentos</td>
                <td>Controle de cobrança e conformidade fiscal</td>
                <td>Obrigação legal e legítimo interesse (incisos II e IX)</td>
              </tr>
              <tr>
                <td>Sessão de autenticação (armazenada localmente no navegador)</td>
                <td>Manter o usuário logado entre sessões</td>
                <td>Execução de contrato (inciso II)</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="bc-legal-section">
          <h2>3. Armazenamento e retenção</h2>
          <p>
            Seus dados são armazenados em servidores seguros gerenciados pelo <strong>Supabase</strong>
            (infraestrutura AWS, região us-east-1, EUA), protegidos por criptografia em repouso e em
            trânsito (HTTPS/TLS).
          </p>
          <ul>
            <li><strong>Dados de conta (email):</strong> enquanto a conta estiver ativa.</li>
            <li><strong>Dados financeiros (transações, cartões):</strong> enquanto a conta estiver ativa; após exclusão, são removidos imediatamente.</li>
            <li><strong>Histórico de pagamentos:</strong> anonimizados (valor e data apenas) por 5 anos após encerramento da assinatura, conforme exigência do Código Tributário Nacional (Art. 195).</li>
          </ul>
        </section>

        <section className="bc-legal-section">
          <h2>4. Compartilhamento e transferências internacionais</h2>
          <p>Compartilhamos dados apenas com os seguintes parceiros necessários para a operação do serviço:</p>
          <ul>
            <li>
              <strong>Supabase, Inc. (EUA):</strong> banco de dados, autenticação e funções de backend.
              Operado em servidores AWS nos Estados Unidos. A transferência ocorre com base em cláusulas
              contratuais padrão e no Data Processing Agreement (DPA) disponível em{' '}
              <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer">supabase.com/privacy</a>.
            </li>
            <li>
              <strong>Mercado Pago (Argentina/Brasil):</strong> processamento de pagamentos e assinaturas
              recorrentes. Apenas seu e-mail é enviado ao Mercado Pago para a criação de assinaturas.
              Consulte a política de privacidade do Mercado Pago em{' '}
              <a href="https://www.mercadopago.com.br/privacidade" target="_blank" rel="noopener noreferrer">mercadopago.com.br/privacidade</a>.
            </li>
          </ul>
          <p>Não vendemos, alugamos ou compartilhamos seus dados com terceiros para fins de marketing ou publicidade.</p>
        </section>

        <section className="bc-legal-section">
          <h2>5. Seus direitos (Art. 18 da LGPD)</h2>
          <p>Você tem os seguintes direitos em relação aos seus dados pessoais:</p>
          <ul>
            <li><strong>Acesso:</strong> solicitar confirmação e acesso aos dados que mantemos sobre você.</li>
            <li><strong>Portabilidade:</strong> exportar todos os seus dados financeiros em formato JSON (disponível em Configurações → Exportar dados).</li>
            <li><strong>Correção:</strong> atualizar dados incorretos diretamente no aplicativo.</li>
            <li><strong>Exclusão:</strong> excluir sua conta e todos os dados associados (disponível em Configurações → Excluir conta). Dados de pagamento são anonimizados conforme prazo legal.</li>
            <li><strong>Revogação do consentimento:</strong> você pode solicitar a exclusão da conta a qualquer momento.</li>
            <li><strong>Informação sobre compartilhamento:</strong> esta política lista todos os terceiros com quem compartilhamos dados.</li>
            <li><strong>Oposição:</strong> contestar tratamentos realizados com base em legítimo interesse.</li>
          </ul>
          <p>
            Para exercer seus direitos, acesse as Configurações do aplicativo ou envie e-mail para{' '}
            <a href="mailto:privacidade@bagcash.app">privacidade@bagcash.app</a>.
            Respondemos em até 15 dias úteis.
          </p>
        </section>

        <section className="bc-legal-section">
          <h2>6. Segurança</h2>
          <p>Adotamos as seguintes medidas técnicas e organizacionais para proteger seus dados:</p>
          <ul>
            <li>Criptografia em trânsito (HTTPS/TLS) em todas as comunicações.</li>
            <li>Criptografia em repouso nos servidores do Supabase.</li>
            <li>Autenticação via JWT com tokens de curta duração.</li>
            <li>Senhas armazenadas com hash bcrypt (nunca em texto puro).</li>
            <li>Controle de acesso por Row Level Security (RLS) no banco de dados — cada usuário acessa apenas seus próprios dados.</li>
            <li>Verificação de integridade de webhooks via HMAC-SHA256.</li>
            <li>Apenas os últimos 4 dígitos do cartão de crédito são armazenados — nunca o número completo.</li>
          </ul>
        </section>

        <section className="bc-legal-section">
          <h2>7. Cookies e armazenamento local</h2>
          <p>
            O BagCash não utiliza cookies de rastreamento ou publicidade. A sessão de autenticação é
            armazenada no <code>localStorage</code> do seu navegador pelo SDK do Supabase para mantê-lo
            logado entre visitas. Você pode limpar esse dado a qualquer momento nas configurações do
            navegador, o que encerrará sua sessão.
          </p>
          <p>Não utilizamos Google Analytics, Meta Pixel nem qualquer outro serviço de rastreamento de comportamento.</p>
        </section>

        <section className="bc-legal-section">
          <h2>8. Incidentes de segurança</h2>
          <p>
            Em caso de incidente de segurança que possa acarretar risco ou dano relevante aos titulares,
            notificaremos a Autoridade Nacional de Proteção de Dados (ANPD) e os titulares afetados
            no prazo de 2 (dois) dias úteis a partir do conhecimento do incidente, conforme exigido
            pelo Art. 48 da LGPD.
          </p>
        </section>

        <section className="bc-legal-section">
          <h2>9. Encarregado de proteção de dados (DPO)</h2>
          <p>
            O responsável pelo tratamento de dados pessoais é <strong>Paulo Santos</strong>,
            acessível pelo e-mail <a href="mailto:privacidade@bagcash.app">privacidade@bagcash.app</a>.
          </p>
        </section>

        <section className="bc-legal-section">
          <h2>10. Alterações nesta política</h2>
          <p>
            Podemos atualizar esta Política periodicamente. Alterações relevantes serão comunicadas
            por e-mail ou por aviso no aplicativo com antecedência mínima de 15 dias. A data de
            vigência no topo desta página indica a versão em vigor.
          </p>
        </section>

        <section className="bc-legal-section">
          <h2>11. Lei aplicável e foro</h2>
          <p>
            Esta Política é regida pela lei brasileira, em especial pela LGPD (Lei 13.709/2018).
            Fica eleito o foro da Comarca de São Paulo/SP para dirimir eventuais controvérsias.
          </p>
        </section>

        <div className="bc-legal-contact">
          <p>Dúvidas? Fale conosco: <a href="mailto:privacidade@bagcash.app">privacidade@bagcash.app</a></p>
        </div>
      </div>
    </div>
  )
}
